import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SiteShell } from "@/components/site-shell";
import { initiatives } from "@/data/sample";

export const Route = createFileRoute("/volunteer")({
  head: () => ({
    meta: [
      { title: "Volunteer — Manavta" },
      { name: "description", content: "Volunteer with Manavta across seven initiatives." },
      { property: "og:title", content: "Volunteer — Manavta" },
      {
        property: "og:description",
        content: "Sign up to volunteer with any of our seven initiatives.",
      },
    ],
  }),
  component: Page,
});

function Page() {
  const [submitted, setSubmitted] = useState(false);
  return (
    <SiteShell>
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
            Volunteer
          </p>
          <h1 className="mt-2 font-display text-4xl font-semibold text-[var(--maroon-deep)]">
            Join 1,850+ volunteers on the ground
          </h1>
          <p className="mt-3 text-foreground/70">
            Weekend gau-seva shifts, bird rescues during Uttarayan, teaching a Balwadi class — we
            have roles for every skill and schedule.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              "Weekend shifts at gaushalas and shelters in Ahmedabad",
              "Skill-based volunteering — design, tech, medical, legal",
              "Corporate volunteering days for CSR partners",
              "Student internships (min. 4 weeks) with letter of experience",
            ].map((x) => (
              <li key={x} className="flex gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[var(--saffron)]" /> {x}
              </li>
            ))}
          </ul>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
          }}
          className="space-y-4 rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-soft)]"
        >
          {submitted ? (
            <div className="rounded-xl bg-[var(--cream-2)] p-6 text-center">
              <p className="font-display text-xl font-semibold text-[var(--maroon-deep)]">
                Thank you 🙏
              </p>
              <p className="mt-2 text-sm text-foreground/70">
                Our team will get in touch within 3 working days.
              </p>
            </div>
          ) : (
            <>
              <Field label="Full name" required />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email" type="email" required />
                <Field label="Mobile" required />
              </div>
              <Field label="City" required />
              <label className="block">
                <span className="text-xs text-foreground/60">Interested initiative *</span>
                <select
                  required
                  className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-[var(--maroon)]"
                >
                  <option value="">Choose an initiative</option>
                  {initiatives.map((i) => (
                    <option key={i.slug} value={i.slug}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-foreground/60">How would you like to help?</span>
                <textarea
                  rows={4}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-[var(--maroon)]"
                />
              </label>
              <button
                type="submit"
                className="w-full rounded-full bg-[var(--maroon)] px-5 py-3 text-sm font-semibold text-[var(--cream)] hover:bg-[var(--maroon-deep)]"
              >
                Sign up to volunteer
              </button>
            </>
          )}
        </form>
      </section>
    </SiteShell>
  );
}

function Field({
  label,
  type = "text",
  required,
}: {
  label: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs text-foreground/60">
        {label}
        {required && " *"}
      </span>
      <input
        type={type}
        required={required}
        className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-[var(--maroon)]"
      />
    </label>
  );
}
